import React, { useState, useEffect } from 'react';
import './SendMessage.css';
import ManagerSidebar from '../components/Sidebar/ManagerSidebar';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperPlane, faEnvelope, faEdit, faTrash,
  faCalendarAlt, faArrowLeft, faPlus, faTimes
} from '@fortawesome/free-solid-svg-icons';

export default function Notification() {
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('pending');
  const [isComposing, setIsComposing] = useState(false);
  const [messages, setMessages] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editStatus, setEditStatus] = useState('pending');
  const [isLoading, setIsLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  useEffect(() => {
    fetchMessages();
  }, []);

  // Auto-hide feedback after 3 seconds
  useEffect(() => {
    if (feedback) {
      const timer = setTimeout(() => setFeedback(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [feedback]);

  const fetchMessages = async () => {
    try {
      const response = await fetch('https://ikaze-thouse-backend.vercel.app/api/v1/notifications/get-message');
      const data = await response.json();
      if (data.success) {
        const formatted = data.messages.map(msg => ({
          ...msg,
          date: new Date(msg.created_at || Date.now()).toLocaleString('en-US', {
            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
          }),
          text: msg.message || msg.message_text || '',
          status: msg.status || 'pending',
          id: msg.id || msg.message_id,
        }));
        setMessages(formatted);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSend = async () => {
    if (!message.trim()) return;
    setIsLoading(true);
    try {
      const response = await fetch('https://ikaze-thouse-backend.vercel.app/api/v1/notifications/send-message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, status }),
      });
      const data = await response.json();
      if (data.success) {
        fetchMessages(); // Refresh to get server-side sorting
        setMessage('');
        setIsComposing(false);
        setFeedback({ type: 'success', text: 'Notification broadcasted!' });
      }
    } catch (error) {
      setFeedback({ type: 'error', text: 'Connection failed.' });
    } finally {
      setIsLoading(false);
    }
  };

  const statusCounts = messages.reduce(
    (counts, msg) => {
      const key = (msg.status || 'pending').toLowerCase();
      if (counts[key] !== undefined) counts[key]++;
      return counts;
    },
    { pending: 0, fixed: 0, failed: 0 }
  );

  const totalPages = Math.ceil(messages.length / itemsPerPage);
  const currentMessages = messages.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <div className="dashboard-wrapper">
      <ManagerSidebar />
      
      <main className="main-content">
        <header className="page-header">
          <div className="header-left">
            <h1><FontAwesomeIcon icon={faEnvelope} className="icon-gradient" /> Notifications</h1>
            <p className="subtitle">Manage and broadcast updates to your residents</p>
          </div>
          <div className="header-actions">
            <button className="btn-secondary" onClick={() => navigate('/notifctToTena')}>
              <FontAwesomeIcon icon={faArrowLeft} /> Back
            </button>
            <button className={`btn-primary ${isComposing ? 'active' : ''}`} onClick={() => setIsComposing(!isComposing)}>
              <FontAwesomeIcon icon={isComposing ? faTimes : faPlus} /> {isComposing ? 'Cancel' : 'New Message'}
            </button>
          </div>
        </header>

        <div className="stats-grid">
          <div className="stat-card pending">
            <span className="label">Pending</span>
            <span className="value">{statusCounts.pending}</span>
          </div>
          <div className="stat-card fixed">
            <span className="label">Resolved</span>
            <span className="value">{statusCounts.fixed}</span>
          </div>
          <div className="stat-card failed">
            <span className="label">Issues</span>
            <span className="value">{statusCounts.failed}</span>
          </div>
        </div>

        {isComposing && (
          <section className="compose-card animate-slide-down">
            <h3>Compose New Broadcast</h3>
            <textarea
              placeholder="What would you like to notify everyone about?"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
            <div className="compose-footer">
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="pending">🟡 Priority: Pending</option>
                <option value="fixed">🟢 Priority: Fixed</option>
                <option value="failed">🔴 Priority: Critical</option>
              </select>
              <button className="btn-send" onClick={handleSend} disabled={isLoading || !message}>
                {isLoading ? 'Processing...' : <><FontAwesomeIcon icon={faPaperPlane} /> Send Message</>}
              </button>
            </div>
          </section>
        )}

        {feedback && <div className={`toast ${feedback.type}`}>{feedback.text}</div>}

        <section className="messages-container">
          <div className="section-title">
            <FontAwesomeIcon icon={faCalendarAlt} />
            <h2>Recent Activity</h2>
          </div>

          {messages.length === 0 ? (
            <div className="empty-state">No notifications sent yet.</div>
          ) : (
            <div className="messages-grid">
              {currentMessages.map(msg => (
                <div key={msg.id} className="message-item">
                  <div className="msg-header">
                    <span className={`badge ${msg.status.toLowerCase()}`}>{msg.status}</span>
                    <span className="msg-date">{msg.date}</span>
                  </div>
                  <p className="msg-body">{msg.text}</p>
                  <div className="msg-actions">
                    <button className="action-btn edit" onClick={() => console.log('Edit', msg.id)}>
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button className="action-btn delete" onClick={() => setMessages(messages.filter(m => m.id !== msg.id))}>
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="pagination">
              <button disabled={currentPage === 1} onClick={() => setCurrentPage(prev => prev - 1)}>Prev</button>
              <span className="page-info">Page {currentPage} of {totalPages}</span>
              <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(prev => prev + 1)}>Next</button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}